import { Range, RangeStatus, ReadinessStatus } from '../../app/services/api/rangeOverview/rangeOverviewTypes';

/** Seed data for the Range Planning "Range Overview" control tower - derived from the Thomas
 * Franks "The British Kitchen" range (single range, replacing the previous 9 demo-only
 * ranges). This range is built from the real "Autumn at the Table" menu (menuId 1, see
 * menusData.ts), so its sites/service period line up with what Menus/Recipes show elsewhere.
 *
 * RangeStatus mapping: the source `status` is "Approved for trial" - the range has been
 * signed off by the culinary/commercial teams but the 6-site trial has not gone live yet
 * (trial.startDate is in the future). RangeStatus.InReview is the closest existing enum value
 * (it is past Draft/Approved-for-drafting but not yet Scheduled/Live) - RangeStatus.Approved
 * was considered but InReview better reflects that a trial review (menus[0].trial.reviewDate)
 * still gates full rollout. */
export const ranges: Range[] = [
  {
    id: 1,
    name: 'The British Kitchen',
    menuId: 1,
    customerName: 'Thomas Franks Workplace Dining Clients (British Kitchen Trial)',
    clientAccount: 'Thomas Franks Workplace Dining',
    siteGroup: 'British Kitchen Trial Sites',
    sites: [
      { siteName: 'Riverside Corporate Campus (BRK-01)', region: 'London' },
      { siteName: 'Northgate Business Park (BRK-02)', region: 'North West' },
      { siteName: 'Midland Technology Quarter (BRK-03)', region: 'Midlands' },
      { siteName: 'Harbourside Office Village (BRK-04)', region: 'South West' },
      { siteName: 'Castlefield Financial Centre (BRK-05)', region: 'Scotland' },
      { siteName: 'Eastfield Innovation Hub (BRK-06)', region: 'East of England' },
    ],
    servicePeriod: 'Lunch',
    activeFrom: '2026-09-01',
    activeTo: '2026-11-30',
    status: RangeStatus.InReview,
    costStatus: ReadinessStatus.Ready,
    costStatusNote: 'Average food cost tracking at 32%, within the range\'s 35% maximum food cost principle.',
    allergenStatus: ReadinessStatus.Attention,
    allergenStatusNote: '19 of 25 British Kitchen recipes are catalogue summaries pending full allergen authoring - estimated allergen data is in use until recipe development completes.',
    carbonStatus: ReadinessStatus.Ready,
    carbonStatusNote: 'All 25 recipes carry a carbon rating (6 authored from supplier/ingredient data, 19 estimated from dietary position pending full sustainability data).',
    lastPublished: null,
    code: 'RNG-BRK-001',
    type: 'Core workplace dining offer',
    owner: 'Culinary Development Team',
    serviceModel: ['Staff restaurant','Workplace hospitality'],
    availableFrom: '2026-09-01',
    availableUntil: '2026-11-30',
    season: 'Autumn',
    targetAudience: 'Professional workplace diners',
    pricePosition: 'Premium everyday dining',
    description: 'A seasonal collection of modern British dishes showcasing responsibly sourced meat, British-grown vegetables and carefully selected regional ingredients. The range balances familiar comfort with contemporary presentation and lighter, plant-forward choices.',
    principles: ['Freshly prepared on site','Seasonal British produce','At least 40% plant-based dishes','Red Tractor or equivalent meat assurance','Sustainable seafood only','Maximum target food cost of 35%','Cross-utilisation of core ingredients','Suitable for mixed workplace demographics','Clear allergen and nutritional information','Designed to minimise avoidable waste'],
    targets: {
      'averageSellingPrice': 7.35,
      'averageFoodCostPercentage': 32,
      'grossMarginPercentage': 68,
      'plantBasedDishPercentage': 40,
      'britishSourcedIngredientPercentage': 75,
      'localSourcingWithinMiles': 50,
      'localSourcingPercentage': 30,
      'maximumWastePercentage': 5,
      'customerSatisfactionScore': 4.4,
      'repeatPurchasePercentage': 35,
    },
  },
];
